import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  getGerpProductionDetailUrl,
  getProductionCostClosingDraftSourceUrl,
} from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import useLayoutSize from '@/hooks/useLayoutSize';
import { getNestedDataFetcher } from '@/utils/apis';
import type {
  ProductionCostClosingDetail,
  ProductionCostClosingDraftSource,
} from '@/utils/approval/productionCostClosing';
import { DETAIL_BUTTON_CLASS } from './content';
import ProductionCostClosingDetailTable from './DetailTable';
import {
  ProductionCostClosingDetailSheetBody,
  ProductionCostClosingRoot,
} from './styles';

export { buildProductionCostClosingContentHtml } from './content';

/**
 * 상세보기 조회 경로 — 작성 중 「확인」한 건은 draft-source(마감 월),
 * 저장된 문서(편집·조회)는 문서번호(gerp-detail, 상신 건은 미상신 목록에서 빠짐)
 */
export type ProductionCostClosingDetailSource =
  | { kind: 'draft'; yyyymm: string }
  | { kind: 'document'; documentId: string };

type DetailTarget = {
  approvalId: string;
  invId: string;
  division: string;
  maker: string;
};

/** 레거시 문서는 버튼 class 가 btn_detail(국내)·btn_detail_show(직송) 두 가지 */
const isDetailButton = (element: Element) =>
  element.id === 'btn_detail_show' ||
  element.classList.contains(DETAIL_BUTTON_CLASS) ||
  element.classList.contains('btn_detail');

const readTarget = (element: Element, fallbackApprovalId: string) => ({
  approvalId: element.getAttribute('data-aprv_id') || fallbackApprovalId,
  invId: element.getAttribute('data-inv_id') ?? '',
  division: element.getAttribute('data-division') ?? '',
  maker: element.getAttribute('data-maker') ?? '',
});

const fetchDetail = async (
  source: ProductionCostClosingDetailSource,
  target: DetailTarget,
): Promise<ProductionCostClosingDetail | null> => {
  if (source.kind === 'draft') {
    const data = await getNestedDataFetcher<ProductionCostClosingDraftSource>(
      getProductionCostClosingDraftSourceUrl({
        yyyymm: source.yyyymm,
        approvalId: target.approvalId,
        invId: target.invId,
      }),
    );
    return data.display.detail;
  }
  const data = await getNestedDataFetcher<{
    detail: ProductionCostClosingDetail | null;
  }>(
    getGerpProductionDetailUrl(source.documentId, {
      approvalId: target.approvalId,
      invId: target.invId,
    }),
  );
  return data.detail;
};

type DetailState = {
  target: DetailTarget;
  detail: ProductionCostClosingDetail | null;
};

/** 「상세 내역」 — PC 모달, 모바일 바텀시트 */
const DetailDialog = ({
  state,
  onClose,
}: {
  state: DetailState | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);
  const open = state !== null;

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = state ? (
    <ProductionCostClosingRoot>
      {state.detail ? (
        <ProductionCostClosingDetailTable
          lines={state.detail.lines ?? []}
          division={state.target.division}
          maker={state.target.maker}
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Spinner theme="black" size="24px" />
        </Box>
      )}
    </ProductionCostClosingRoot>
  ) : null;

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={t('상세 내역')}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <ProductionCostClosingDetailSheetBody>
          {body}
        </ProductionCostClosingDetailSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal
      ref={modalRef}
      title={t('상세 내역')}
      handleClose={onClose}
      isExpandable
    >
      <>{body}</>
    </Modal>
  );
};

/** 작성·조회 공용 — 저장 본문(레거시 문서 포함) 그대로, 행의 「상세보기」는 그때 다시 조회해 창으로 */
export const ProductionCostClosingSnapshot = ({
  content,
  detailSource,
  approvalId,
}: {
  content: string;
  /** 없으면 상세보기 버튼을 그리지 않음 */
  detailSource: ProductionCostClosingDetailSource | null;
  /** 버튼에 결재 건이 없을 때 쓸 저장 attributes.approvalId */
  approvalId: string;
}) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [detailState, setDetailState] = useState<DetailState | null>(null);
  // 닫은 뒤 늦게 온 응답이 창을 다시 열지 않게
  const requestIdRef = useRef(0);

  const close = useCallback(() => {
    requestIdRef.current += 1;
    setDetailState(null);
  }, []);

  const openDetail = useCallback(
    async (target: DetailTarget) => {
      if (!detailSource) return;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setDetailState({ target, detail: null });
      try {
        const detail = await fetchDetail(detailSource, target);
        if (requestId !== requestIdRef.current) return;
        if (!detail) throw new Error('detail missing');
        setDetailState({ target, detail });
      } catch {
        if (requestId !== requestIdRef.current) return;
        setDetailState(null);
        showError({ message: t('데이터를 불러오지 못했습니다.') });
      }
    },
    [detailSource, showError, t],
  );

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      if (tagName !== 'button' || !isDetailButton(element)) return undefined;
      if (!detailSource) return null;
      const target = readTarget(element, approvalId);
      return (
        <BoxButton
          key={props.key as string}
          type="button"
          variant="secondary"
          size="small"
          onClick={() => openDetail(target)}
        >
          {t('상세보기')}
        </BoxButton>
      );
    },
    [approvalId, detailSource, openDetail, t],
  );

  return (
    <>
      <ProductionCostClosingRoot>
        <SerpContentSnapshot content={content} transform={transform} />
      </ProductionCostClosingRoot>
      <DetailDialog state={detailState} onClose={close} />
    </>
  );
};
